import { RecordKind } from '../record.js';
import { SourcedValue } from '../option.js';
import { equableTypeGuard, MLString } from '../types.js';
import fastDeepEqual from 'fast-deep-equal';
export class EngineUtils {
    flattenProducts(root) {
        let list = [];
        for (let child of root.children) {
            if (child.kind === RecordKind.Product) {
                list.push(child);
            }
            else {
                list.push(...this.flattenProducts(child));
            }
        }
        return list;
    }
    flattenOptionGroup(root) {
        let list = [];
        for (let child of root.children) {
            if (child.kind === RecordKind.Option) {
                list.push(child);
            }
            else {
                list.push(...this.flattenOptionGroup(child));
            }
        }
        return list;
    }
    flattenOptions(root) {
        const result = [];
        root.forEach(next => {
            if (next.kind === RecordKind.OptionGroup) {
                result.push(...this.flattenOptionGroup(next));
            }
            else {
            }
            if (next.kind === RecordKind.Option) {
                result.push(next);
            }
        });
        return result;
    }
    getAvailableValues(item, selector) {
        const potentialSourcedValue = selector(item);
        if (potentialSourcedValue instanceof SourcedValue) {
            return this.flattenOptions(potentialSourcedValue.getSource());
        }
        else {
            return [];
        }
    }
    flattenSourcedValues(list, selector) {
        const result = [];
        for (let item of list) {
            const options = this.getAvailableValues(item, selector);
            result.push(...options);
        }
        return result;
    }
    cartesianProduct(optionsPotentialValues, handler) {
        if (!optionsPotentialValues.size) {
            handler([]);
            return;
        }
        const vector = [];
        const options = [];
        let i = 0;
        for (const [optionPropertyPath, optionValues] of optionsPotentialValues) {
            vector[i] = 0;
            options[i] = { optionPropertyPath, optionValues };
            i++;
        }
        let j;
        while (true) {
            const values = options.map((pair, i) => {
                return ({
                    optionPropertyPath: pair.optionPropertyPath,
                    option: pair.optionValues[vector[i]]
                });
            });
            const exitExpected = handler(values);
            if (exitExpected === true) {
                return;
            }
            j = vector.length - 1;
            while (true) {
                vector[j]++;
                if (vector[j] < options[j].optionValues.length) {
                    break;
                }
                vector[j] = 0;
                j--;
                if (j < 0) {
                    return;
                }
            }
        }
    }
    cartesian(initials, fns, finalizer, breakHandler = () => { }) {
        const q = [initials.slice()];
        const result = [];
        while (q.length) {
            const next = q.length - 1;
            const nextGroup = q[next];
            if (nextGroup.length > 0) {
                const value = nextGroup.shift();
                if (next >= fns.length) {
                    result.push(finalizer(value, q));
                }
                else {
                    const values = fns[next](value, q);
                    q.push(values);
                }
            }
            else {
                breakHandler(next, q);
                q.pop();
            }
        }
        return result;
    }
    equals(left, right) {
        if (left && right && equableTypeGuard(left) && equableTypeGuard(right)) {
            return left.eq(right);
        }
        if (typeof left === 'object' || typeof right === 'object') {
            return fastDeepEqual(left, right);
        }
        return left == right;
    }
    getPropertyPathValue(obj, propertyPath) {
        const names = propertyPath.split('.');
        let currentValue = obj;
        for (let name of names) {
            if (currentValue !== undefined) {
                currentValue = currentValue[name];
            }
        }
        return currentValue;
    }
    objectDiff(oldObject, newObject, properties = []) {
        let result = {};
        for (const prop of properties) {
            const oldValue = this.getPropertyPathValue(oldObject, prop);
            const newValue = this.getPropertyPathValue(newObject, prop);
            if (oldValue === undefined) {
                result[prop] = {
                    oldValue: null,
                    newValue: newValue
                };
                continue;
            }
            if (newValue === undefined) {
                result[prop] = {
                    oldValue: oldValue,
                    newValue: null
                };
                continue;
            }
            if (!this.equals(oldValue, newValue)) {
                result[prop] = {
                    oldValue: oldValue,
                    newValue: newValue
                };
            }
        }
        return result;
    }
    distinct(data, columns) {
        const dataset = [];
        const visited = new Set();
        for (let item of data) {
            const record = [];
            for (let i = 0; i < columns.length; i++) {
                let column = columns[i];
                if (typeof column === 'string') {
                    record[i] = item[column];
                }
                else {
                    record[i] = column(item);
                }
            }
            if (!isRecordInDataset(visited, record, dataset)) {
                dataset.push(record);
                visited.add(getHash(record));
            }
        }
        return dataset;
    }
    groupBy(items, keySelector) {
        const map = new Map();
        for (let item of items) {
            const key = keySelector(item);
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key).push(item);
        }
        return map;
    }
    getPropertyPathValues(model, properties) {
        const result = [];
        for (const property of properties) {
            result.push(this.getPropertyPathValue(model, property));
        }
        return result;
    }
    buildObjectByKeys(value, keys) {
        if (value.length !== keys.length) {
            throw new Error('Invalid number of columns');
        }
        let obj = {};
        for (let j = 0; j < value.length; j++) {
            obj[keys[j]] = value[j];
        }
        return obj;
    }
    compareStrings(itemA, itemB, ignoreCase = false) {
        if (ignoreCase) {
            itemA = itemA.toUpperCase();
            itemB = itemB.toUpperCase();
        }
        const itemAParsedInt = Number.parseInt(itemA, 10);
        const itemBParsedInt = Number.parseInt(itemB, 10);
        if (Number.isFinite(itemAParsedInt) &&
            Number.isFinite(itemBParsedInt) &&
            itemAParsedInt !== itemBParsedInt) {
            return itemAParsedInt > itemBParsedInt ? 1 : -1;
        }
        if (itemA.length !== itemB.length) {
            let equalSubstring = '';
            let charIndex = 0;
            while (itemA[charIndex] === itemB[charIndex]) {
                equalSubstring += itemA[charIndex];
                charIndex++;
            }
            const equalSubstringLength = equalSubstring.length;
            if (equalSubstringLength) {
                const diffSubstringA = itemA.substr(equalSubstringLength);
                const diffSubstringB = itemB.substr(equalSubstringLength);
                const diffSubstringAParsedInt = Number.parseInt(diffSubstringA, 10);
                const diffSubstringBParsedInt = Number.parseInt(diffSubstringB, 10);
                if (diffSubstringAParsedInt && diffSubstringBParsedInt) {
                    return diffSubstringAParsedInt > diffSubstringBParsedInt ? 1 : -1;
                }
            }
        }
        return itemA === itemB ? 0 : (itemA > itemB ? 1 : -1);
    }
    checkIfMLStringAndCompare(languageService, itemA, itemB, ignoreCase = false) {
        const valueA = itemA instanceof MLString ? languageService.translateOptional(itemA) : itemA;
        const valueB = itemB instanceof MLString ? languageService.translateOptional(itemB) : itemB;
        return engineUtils.compareStrings(valueA, valueB, ignoreCase);
    }
    pipe(input, ...fns) {
        let result = input;
        for (let fn of fns) {
            result = fn(result);
        }
        return result;
    }
    fromFirstOrDefault(xs, selector, defaultValue) {
        if (xs.length) {
            return selector(xs[0]);
        }
        else
            return defaultValue;
    }
    clearArray(xs) {
        while (xs.length > 0) {
            xs.pop();
        }
        return xs;
    }
}
EngineUtils.seed = 0xABCD;
EngineUtils.hash = (v) => {
    return JSON.stringify(v);
};
function isRecordInDataset(visited, record, dataset) {
    return visited.has(this.getHash(record));
}
function getHash(record) {
    return record.join('##');
}
export const engineUtils = new EngineUtils();
//# sourceMappingURL=EngineUtils.js.map